import React, { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { GroupSummaryStore, GroupType } from 'src/stores/groups-store';

const GroupSelect: FC<
  {
    onChange?: (value: { value: string; label: string }, organization?: string) => void;
    exclude?: string[];
    value?: { value: string; label: string };
    filter?: string;
  } & WithTranslation
> = ({ t, onChange, exclude, value, filter }) => {
  const groupStore = container.get(GroupSummaryStore);
  const groupsState = groupStore.state;
  const [currentValue, setValue] = useState(value);

  const load = (search: string | undefined = undefined) => {
    const queryFilter = (exclude || []).length === 0 ? [] : [`id ne ${exclude?.join(' and id ne ')}`];
    if (filter) queryFilter.push(filter);
    groupStore.loadWithTypes(
      {
        searchQuery: search,
        orderBy: [{ field: 'name', direction: 'Ascending', useProfile: false }],
        skip: 0,
        take: 1000000,
        filter: queryFilter,
      } as Query,
      [GroupType.Default]
    );
  };

  useEffect(() => {
    let nextValue = value || currentValue;
    if (nextValue && (exclude || []).includes(nextValue.value)) nextValue = undefined;
    setValue(nextValue);
    load();
  }, [exclude, groupStore, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = debounce((value: string) => {
    load(value);
  }, 800);

  const onSelect = (nextValue: { value: string; label: string }, option: any) => {
    setValue(nextValue);
    onChange && onChange(nextValue, option?.organization);
  };

  return (
    <Select
      loading={!!groupsState.isBusy.get()}
      labelInValue
      placeholder={t('SelectGroup')}
      onChange={onSelect}
      style={{ width: '100%' }}
      allowClear
      showSearch
      value={currentValue}
      notFoundContent={groupsState.isBusy.get() ? <Spin size="small" /> : undefined}
      filterOption={false}
      showArrow={false}
      onSearch={onSearch}
      onDropdownVisibleChange={(visible) => {
        if (!visible) load();
      }}
    >
      {groupsState.items.value.map((o) => (
        <Select.Option key={o.id} value={o.id} organization={o.organizationId}>
          {o.name}
        </Select.Option>
      ))}
    </Select>
  );
};

export default withTranslation()(GroupSelect);
